import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { loadSession, sendMessage } from './api/chat.js';
import { Avatar } from './avatar/Avatar.js';
import { PhotoAvatar } from './avatar/PhotoAvatar.js';
import { CHARACTERS, characterById } from './avatar/character.js';
import { useUserCues } from './avatar/userCues.js';
import { CartoonAdvisor } from './avatar/CartoonAdvisor.js';
import { VoiceOrb } from './avatar/VoiceOrb.js';
import { BASE_PATH, GENERIC, VARIANT } from './variant.js';
import { useVoice } from './avatar/useVoice.js';
import { useMic } from './speech/useMic.js';
import { Icon } from './ui/Icon.js';
import { MicMeter } from './ui/MicMeter.js';
import { ACCEPT, formatSize, MAX_FILES, readFiles, type PendingFile } from './ui/attachments.js';
import { AtencionHumanaDialog } from './ui/AtencionHumana.js';
import { cedulaEnTexto, HomologacionDialog, OfertaCarreras, OfertaCedula, ResultadoCard, TitulosCard, titulo, type ConsultaTitulos, type Expediente, type InfoHomologacion, type Solicitud } from './ui/Homologacion.js';
import './styles/app.css';

/**
 * Pantalla principal: una videollamada con el asesor.
 *
 * A la izquierda (arriba en el movil) el escenario: el avatar a gran tamano,
 * con subtitulos en directo de lo que esta diciendo y un indicador de estado.
 * A la derecha la conversacion, y abajo el compositor: se puede escribir o
 * pulsar el microfono y hablar. Hablar mientras el avatar habla lo interrumpe.
 */

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'error';
  content: string;
  /** Nombres de los documentos adjuntos a este mensaje. */
  files?: string[];
  /** Resultado de una homologacion automatica (tarjeta en la conversacion). */
  homologacion?: Expediente;
  /** Botones para homologar desde el chat con el certificado adjunto: una carrera por boton. */
  ofrecer?: string[];
  /** Carrera de la UMET detectada en el certificado (queda ya elegida). */
  sugerida?: string | null;
  /** Carrera que dice el certificado. */
  carreraOrigen?: string | null;
  /** Se esta leyendo el certificado para preparar la homologacion. */
  leyendo?: boolean;
  /** Ofrece consultar los titulos con la cedula que escribio. */
  consultarCedula?: { cedula: string; malla: string | null; otraVez?: boolean };
  /** Titulos registrados del estudiante (consulta por cedula). */
  titulos?: ConsultaTitulos;
  /** Citas de la fuente oficial que respaldan la respuesta (se ven, no se dicen). */
  sources?: string[];
}

/** La respuesta remite al boton de atencion humana: se ofrece ahi mismo. */
const OFRECE_PERSONA = /hablar con una persona/i;

const SUBTITLES_KEY = 'cec-avatar:subtitulos';

function readSubtitles(): boolean | null {
  try {
    const v = window.localStorage.getItem(SUBTITLES_KEY);
    return v === null ? null : v === 'on';
  } catch {
    return null;
  }
}

/** Pide homologar en el texto del mensaje. */
const PIDE_HOMOLOGAR = /homolog|convalid|reconoc|equivalen/i;

const sinTildes = (s: string) => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toUpperCase();

/**
 * Carreras de la plantilla que el mensaje menciona («enfermería» ->
 * ENFERMERIA 2026). Si no menciona ninguna, o varias, se ofrecen todas.
 */
function carrerasMencionadas(texto: string, mallas: string[]): string[] {
  const t = sinTildes(texto);
  const hit = mallas.filter((m) =>
    sinTildes(m)
      .split(/[^A-Z]+/)
      .filter((w) => w.length >= 5)
      .some((w) => t.includes(w.slice(0, 7))),
  );
  return hit.length === 1 ? hit : mallas;
}

type Suggestion = { text: string; icon: 'doc' | 'clock' | 'steps' | 'cap' | 'award' };

/** Preguntas de la demostracion: coinciden con las respuestas grabadas con su voz. */
const DEMO_SUGGESTIONS: Suggestion[] = [
  { text: '¿Qué requisitos necesito para homologar?', icon: 'doc' },
  { text: '¿Cuánto tarda el proceso?', icon: 'clock' },
  { text: '¿Cómo funciona la homologación?', icon: 'steps' },
];

/** Con la base de conocimiento oficial: una pregunta de cada tema (homologacion, carreras, becas). */
const OFFICIAL_SUGGESTIONS: Suggestion[] = [
  { text: '¿Qué necesito para homologar mis estudios?', icon: 'doc' },
  { text: '¿Qué carreras ofrece la universidad?', icon: 'cap' },
  { text: '¿Qué becas o descuentos puedo tener?', icon: 'award' },
];

/** Lo que dice el asesor cuando alguien le saluda con la mano y ya se presento. */
const WAVE_REPLY = '¡Hola! Aquí estoy. ¿En qué te puedo ayudar?';

/**
 * Tu imagen, pequena y en espejo, mientras la camara esta activa: la persona
 * sabe en todo momento que la camara esta encendida.
 */
function SelfView({ videoRef, on }: { videoRef: MutableRefObject<HTMLVideoElement | null>; on: boolean }) {
  const box = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const video = videoRef.current;
    const el = box.current;
    if (!on || !video || !el) return;
    video.className = 'selfview__video';
    el.appendChild(video);
    return () => {
      if (video.parentElement === el) el.removeChild(video);
    };
  }, [on, videoRef]);
  return <div className="selfview" ref={box} aria-label="Tu cámara" />;
}

const STORAGE_KEY = 'cec-avatar:token';
const CHARACTER_KEY = 'cec-avatar:character';

/** Lee el token del enlace /c/<token> o del almacenamiento del navegador. */
function initialToken(): string | null {
  const fromPath = window.location.pathname.match(/^(?:\/generico|\/caricatura)?\/c\/(.+)$/);
  if (fromPath?.[1]) return decodeURIComponent(fromPath[1]);
  try {
    return window.localStorage.getItem(STORAGE_KEY);
  } catch {
    return null; // modo privado o cookies bloqueadas
  }
}

function readCharacter(): string | null {
  try {
    return window.localStorage.getItem(CHARACTER_KEY);
  } catch {
    return null;
  }
}

/**
 * El selector de presentacion es una herramienta de aprobacion interna, no
 * parte de la interfaz publica. Se muestra solo con ?propuesta=1.
 */
function inReviewMode(): boolean {
  return new URLSearchParams(window.location.search).has('propuesta');
}

/**
 * Incrustado en otra pagina (widget del portal, ?embed=1): cabecera compacta,
 * sin marco, y se habla con la pagina anfitriona por postMessage.
 */
const EMBED = new URLSearchParams(window.location.search).has('embed');

/** Avisa a la pagina anfitriona (widget.js): cerrar, ampliar, reducir. */
const tellHost = (action: 'close' | 'expand' | 'shrink') => {
  if (window.parent !== window) window.parent.postMessage({ type: 'cec-avatar', action }, '*');
};

/**
 * Se puede hablar sin un toque dentro de esta pagina? Dentro del widget, el
 * clic fue en la pagina anfitriona: Chrome lo delega (allow="autoplay") y
 * Safari no. Un contexto de audio recien creado lo dice sin hacer ruido.
 */
function canAutoplay(): boolean {
  try {
    const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return false;
    const probe = new Ctx();
    const ok = probe.state === 'running';
    void probe.close();
    return ok;
  } catch {
    return false;
  }
}

/** Trocea la presentacion en frases, como llegan las del modelo. */
function splitSentences(text: string): string[] {
  return text.split(/(?<=[.!?:])\s+/).map((s) => s.trim()).filter(Boolean);
}

type Status = 'idle' | 'listening' | 'transcribing' | 'thinking' | 'speaking';

const STATUS_LABEL: Record<Status, string> = {
  idle: 'Disponible',
  listening: 'Escuchando',
  transcribing: 'Entendiendo',
  thinking: 'Pensando',
  speaking: 'Hablando',
};

export default function App() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [token, setToken] = useState<string | null>(null);
  const [resumed, setResumed] = useState(false);
  const [characterId, setCharacterId] = useState<string | null>(readCharacter);
  const [copied, setCopied] = useState(false);
  /** Documentos elegidos para el proximo mensaje. */
  const [files, setFiles] = useState<PendingFile[]>([]);
  /** Hay una version nueva publicada: se recarga en cuanto no interrumpa nada. */
  const [nuevaVersion, setNuevaVersion] = useState(false);
  const [fileNotice, setFileNotice] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  /** Presentacion del asesor (la da el servidor) y si ya la dijo. */
  const [intro, setIntro] = useState<string | null>(null);
  /** Marca que muestra la cabecera (configurable en el servidor: BRAND_*). */
  const [brand, setBrand] = useState({ name: 'Asesor Virtual Académico', sub: 'Universidad Metropolitana · Ecuador', short: 'Asesor virtual UMET' });
  const [greeted, setGreeted] = useState(false);
  const introducedRef = useRef(false);
  /** La presentacion espera un toque para sonar (Safari dentro del widget). */
  const [needsTap, setNeedsTap] = useState(false);
  /** Cada presentacion hablada: el asesor dibujado saluda con la mano. */
  const [waveCount, setWaveCount] = useState(0);
  const [expanded, setExpanded] = useState(false);
  /** Homologacion automatica: disponibilidad (servidor) y formulario abierto. */
  const [hInfo, setHInfo] = useState<InfoHomologacion | null>(null);
  const [hOpen, setHOpen] = useState(false);
  /** Certificados enviados en el chat, por mensaje que ofrece homologarlos. */
  const certificados = useRef(new Map<string, { files: PendingFile[]; lecturaId?: string }>());
  /** Carrera que dio la consulta de titulos: el certificado que adjunte despues se homologa en ella. */
  const carreraSugerida = useRef<string | null>(null);
  /** homologar() se define mas abajo; los botones del chat la llaman por aqui. */
  const homologarRef = useRef<(s: Solicitud, desdeChat?: string) => Promise<void>>(async () => undefined);
  /** La pagina anfitriona es un movil: el panel ya ocupa toda la pantalla. */
  const [hostMobile, setHostMobile] = useState(false);
  const pendingOpen = useRef(false);
  /**
   * Con el modelo simulado, o mientras la base de conocimiento no sea la
   * oficial, hay que decirlo bien visible: nadie debe planificar su matricula
   * con respuestas de ejemplo.
   */
  const [simulated, setSimulated] = useState(false);
  /** Hay base de conocimiento oficial cargada (modelo real, fase de pruebas). */
  const [official, setOfficial] = useState(false);
  /** «Hablar con una persona»: dialogo abierto y canales oficiales (HUMAN_CONTACT). */
  const [humanOpen, setHumanOpen] = useState(false);
  const [humanContact, setHumanContact] = useState<string | null>(null);
  /**
   * Subtitulos en directo sobre el avatar. Manda la eleccion de la persona;
   * si no eligio, lo que diga el servidor (SUBTITLES).
   */
  const [subtitles, setSubtitles] = useState<boolean>(() => readSubtitles() ?? false);
  const subtitlesChosen = useRef(readSubtitles() !== null);
  const toggleSubtitles = useCallback(() => {
    subtitlesChosen.current = true;
    setSubtitles((on) => {
      try {
        window.localStorage.setItem(SUBTITLES_KEY, on ? 'off' : 'on');
      } catch {
        /* sin almacenamiento disponible */
      }
      return !on;
    });
  }, []);

  const voice = useVoice();
  const abort = useRef<AbortController | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  const character = useMemo(() => characterById(characterId), [characterId]);
  const reviewMode = useMemo(inReviewMode, []);
  const [illustrated, setIllustrated] = useState(false);
  const fallbackToIllustration = useCallback(() => setIllustrated(true), []);

  useEffect(() => {
    void fetch('/api/health')
      .then(
        (r) =>
          r.json() as Promise<{
            provider?: string;
            demo?: boolean;
            knowledge?: boolean;
            intro?: string;
            brand?: { name: string; sub: string; short: string };
            subtitles?: 'on' | 'off';
            human?: { contact?: string };
          }>,
      )
      .then((h) => {
        if (h.intro) setIntro(h.intro);
        if (h.brand) setBrand(h.brand);
        if (h.human?.contact) setHumanContact(h.human.contact);
        if (!subtitlesChosen.current && h.subtitles) setSubtitles(h.subtitles === 'on');
        setSimulated(h.demo ?? h.provider === 'mock');
        setOfficial(h.provider !== 'mock' && h.knowledge === true);
      })
      .catch(() => setSimulated(false));
    void fetch('/api/homologacion/info')
      .then((r) => (r.ok ? (r.json() as Promise<InfoHomologacion>) : null))
      .then((info) => setHInfo(info))
      .catch(() => setHInfo(null));
  }, []);

  // --- Continuidad: reanudar desde el enlace --------------------------------
  useEffect(() => {
    const saved = initialToken();
    if (!saved) return;

    void loadSession(saved).then((result) => {
      if (!result.ok) {
        // Enlace caducado o invalido: se empieza limpio, sin bloquear al usuario.
        try {
          window.localStorage.removeItem(STORAGE_KEY);
        } catch {
          /* sin almacenamiento disponible */
        }
        window.history.replaceState(null, '', BASE_PATH || '/');
        return;
      }
      setToken(result.data.token);
      setResumed(result.data.messages.length > 0);
      setMessages(
        result.data.messages.map((m, i) => ({
          id: `stored-${i}`,
          role: m.role,
          content: m.content,
          homologacion: m.homologacion,
          sources: m.sources,
        })),
      );
    });
  }, []);

  // Encuadre del avatar: segun la forma del escenario, se elige que franja de
  // la imagen queda a la vista, centrada entre las cejas (30 %) y la barbilla
  // (84 %). Si no cabe entera, manda la boca (70 %): nunca queda fuera.
  useEffect(() => {
    const frame = frameRef.current;
    if (GENERIC || !frame || typeof ResizeObserver === 'undefined') return;
    const IMG_ASPECT = 660 / 849;
    const update = () => {
      const { width, height } = frame.getBoundingClientRect();
      if (!width || !height) return;
      const visible = Math.min(1, IMG_ASPECT / (width / height)); // fraccion vertical visible
      // Escenario bajo y ancho (movil en vertical, panel del widget): llenarlo
      // obligaria a cortar la cabeza y dejar solo ojos y boca. Se muestra la
      // cabeza entera, del pelo a bajo la barbilla (90 % de la imagen), y a los
      // lados el fondo difuminado.
      const HEAD_BAND = 0.9;
      if (visible < 0.86) {
        const h = height / HEAD_BAND;
        frame.classList.add('is-fit');
        frame.style.setProperty('--av-h', `${h.toFixed(1)}px`);
        frame.style.setProperty('--av-w', `${(h * IMG_ASPECT).toFixed(1)}px`);
        return;
      }
      frame.classList.remove('is-fit');
      if (visible >= 0.999) return frame.style.setProperty('--face-y', '50%');
      let top = 0.57 - visible / 2;
      top = Math.min(top, 0.62 - visible * 0.1); // con poca altura, que la boca no quede al borde
      top = Math.max(0, Math.min(1 - visible, top));
      frame.style.setProperty('--face-y', `${((top / (1 - visible)) * 100).toFixed(1)}%`);
    };
    const observer = new ResizeObserver(update);
    observer.observe(frame);
    update();
    return () => observer.disconnect();
  }, []);

  // La conversacion se desplaza dentro de su panel: se lleva el final a la vista.
  useEffect(() => {
    const el = listRef.current;
    if (!el || messages.length === 0) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!characterId) return;
    try {
      window.localStorage.setItem(CHARACTER_KEY, characterId);
    } catch {
      /* sin almacenamiento disponible */
    }
  }, [characterId]);

  /** El campo crece con el texto. Sin esto se escribe a ciegas en una linea. */
  const autoGrow = useCallback(() => {
    const el = fieldRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  }, []);

  // Pestanas abiertas durante horas se quedan con el codigo viejo. Al volver a
  // la pestana y cada 5 minutos se compara el script de esta pagina con el
  // publicado; si cambio, se recarga cuando no haya respuesta en curso ni
  // texto o documentos sin enviar (la conversacion vuelve por su enlace).
  useEffect(() => {
    const propio = [...document.scripts].map((s) => s.src).find((src) => /\/assets\/index-[^/]+\.js$/.test(src));
    if (!propio) return; // en desarrollo no hay script con hash
    const nombre = propio.split('/').pop()!;
    let ultima = 0;
    const revisar = async () => {
      if (Date.now() - ultima < 60_000) return;
      ultima = Date.now();
      try {
        const html = await (await fetch(window.location.pathname, { cache: 'no-store' })).text();
        if (html.includes('/assets/index-') && !html.includes(nombre)) setNuevaVersion(true);
      } catch {
        /* sin red: se intenta en la siguiente */
      }
    };
    const alVolver = () => {
      if (document.visibilityState === 'visible') void revisar();
    };
    document.addEventListener('visibilitychange', alVolver);
    window.addEventListener('focus', alVolver);
    const intervalo = window.setInterval(() => void revisar(), 5 * 60_000);
    return () => {
      document.removeEventListener('visibilitychange', alVolver);
      window.removeEventListener('focus', alVolver);
      window.clearInterval(intervalo);
    };
  }, []);

  useEffect(() => {
    if (nuevaVersion && !busy && !draft.trim() && files.length === 0) window.location.reload();
  }, [nuevaVersion, busy, draft, files.length]);

  /** Lee el certificado al adjuntarlo: carrera detectada y lectura reutilizable. */
  const leerCertificado = useCallback(
    async (
      archivos: PendingFile[],
    ): Promise<{ lecturaId: string; carreraOrigen: string | null; carreraSugerida: string | null } | { error: string }> => {
      try {
        const r = await fetch('/api/homologacion/leer', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ attachments: archivos.map(({ name, mediaType, data }) => ({ name, mediaType, data })) }),
        });
        const b = (await r.json().catch(() => null)) as { lecturaId: string; carreraOrigen: string | null; carreraSugerida: string | null; error?: string } | null;
        if (!r.ok || !b || b.error) return { error: b?.error ?? 'No pude leer el certificado. Inténtalo de nuevo.' };
        return b;
      } catch {
        return { error: 'No pude leer el certificado. Revisa tu conexión e inténtalo de nuevo.' };
      }
    },
    [],
  );

  const send = useCallback(
    async (text: string) => {
      const content = text.trim();
      const outgoing = files;
      if ((!content && outgoing.length === 0) || busy) return;

      setDraft('');
      setFiles([]);
      setFileNotice(null);
      setBusy(true);
      setResumed(false);
      window.requestAnimationFrame(autoGrow);

      // El ORDEN importa. stop() llama a speechSynthesis.cancel(), que vacia la
      // cola del navegador — incluido el enunciado silencioso que unlock() deja
      // encolado para abrir el canal de audio. Si se desbloquea antes de parar,
      // se cancela el propio desbloqueo y en iOS el avatar queda mudo toda la
      // sesion. Primero se corta lo anterior, despues se desbloquea.
      voice.stop();
      voice.unlock();

      const stamp = Date.now();
      const botId = `a-${stamp}`;
      const puedeHomologar = outgoing.length > 0 && !!hInfo?.disponible && hInfo.mallas.length > 0;

      // Escribio su cedula: se ofrece consultar sus titulos en la SENESCYT, sin
      // pasar por el modelo (que no debe ver ni repetir la cedula).
      const cedula = outgoing.length === 0 && hInfo?.verificacion ? cedulaEnTexto(content) : null;
      if (cedula && hInfo) {
        const opciones = carrerasMencionadas(content, hInfo.mallas);
        const malla = opciones.length === 1 ? opciones[0]! : null;
        const oferta = `Con tu cédula puedo consultar tus títulos registrados en la SENESCYT${malla ? ` y decirte cómo se homologarían en ${titulo(malla)}` : ''}.`;
        setMessages((prev) => [
          ...prev,
          { id: `u-${stamp}`, role: 'user', content },
          { id: botId, role: 'assistant', content: oferta, consultarCedula: { cedula, malla } },
        ]);
        for (const sentence of splitSentences(oferta)) voice.enqueue(sentence);
        setBusy(false);
        return;
      }

      // Certificado adjunto + «homologa mis estudios»: se ofrece al momento, en
      // la conversacion, sin pasar por el modelo. El boton de la carrera es a la
      // vez la autorizacion para registrar el expediente.
      // Con la carrera que dio su titulo (consulta por cedula), cualquier
      // certificado que adjunte se ofrece homologar directamente en ella.
      const sugerida = carreraSugerida.current && hInfo?.mallas.includes(carreraSugerida.current) ? carreraSugerida.current : null;
      if (puedeHomologar && (PIDE_HOMOLOGAR.test(content) || sugerida)) {
        // Primero se lee el certificado: la carrera sale de el y queda ya elegida.
        setMessages((prev) => [
          ...prev,
          { id: `u-${stamp}`, role: 'user', content, files: outgoing.map((f) => f.name) },
          { id: botId, role: 'assistant', content: 'Estoy leyendo tu certificado para ver tu carrera. Puede tardar hasta medio minuto.', leyendo: true },
        ]);
        voice.enqueue('Estoy leyendo tu certificado. Puede tardar hasta medio minuto.');
        const lectura = await leerCertificado(outgoing);
        if ('error' in lectura) {
          setMessages((prev) => [...prev.filter((m) => m.id !== botId), { id: `e-${Date.now()}`, role: 'error', content: lectura.error }]);
          setBusy(false);
          return;
        }
        const mencionadas = carrerasMencionadas(content, hInfo.mallas);
        const detectada = lectura.carreraSugerida && hInfo.mallas.includes(lectura.carreraSugerida) ? lectura.carreraSugerida : null;
        const elegida = detectada ?? (mencionadas.length === 1 ? mencionadas[0]! : sugerida);
        const origen = lectura.carreraOrigen;
        const oferta = elegida
          ? `${origen ? `Tu certificado es de ${titulo(origen)}. ` : ''}Te homologo en ${titulo(elegida)}: confírmalo abajo.`
          : `Leí tu certificado${origen ? ` de ${titulo(origen)}` : ''}. Elige la carrera de la UMET a la que quieres ingresar.`;
        certificados.current.set(botId, { files: outgoing, lecturaId: lectura.lecturaId });
        setMessages((prev) =>
          prev.map((m) =>
            m.id === botId ? { ...m, content: oferta, ofrecer: hInfo.mallas, sugerida: elegida, carreraOrigen: origen, leyendo: false } : m,
          ),
        );
        for (const sentence of splitSentences(oferta)) voice.enqueue(sentence);
        setBusy(false);
        return;
      }

      // Documento adjunto sin pedir homologar: mientras el asesor lo revisa, se
      // lee para tener la carrera lista en la oferta de debajo.
      const lecturaP = puedeHomologar ? leerCertificado(outgoing) : null;

      setMessages((prev) => [
        ...prev,
        { id: `u-${stamp}`, role: 'user', content, files: outgoing.length ? outgoing.map((f) => f.name) : undefined },
        { id: botId, role: 'assistant', content: '' },
      ]);

      const controller = new AbortController();
      abort.current = controller;
      let fallo = false;

      await sendMessage({
        message: content,
        attachments: outgoing.map(({ name, mediaType, data }) => ({ name, mediaType, data })),
        greeted: greeted && !token,
        token,
        signal: controller.signal,
        onSession: (event) => {
          setToken(event.token);
          try {
            window.localStorage.setItem(STORAGE_KEY, event.token);
          } catch {
            /* sin almacenamiento disponible */
          }
        },
        onDelta: (chunk) => {
          setMessages((prev) =>
            prev.map((m) => (m.id === botId ? { ...m, content: m.content + chunk } : m)),
          );
        },
        // Cada frase cerrada entra en la cola de voz en cuanto esta lista, sin
        // esperar al final de la respuesta.
        onSentence: (_index, sentence) => voice.enqueue(sentence),
        onSources: (items) => {
          setMessages((prev) => prev.map((m) => (m.id === botId ? { ...m, sources: items } : m)));
        },
        onError: (problem) => {
          fallo = true;
          setMessages((prev) => [
            ...prev.filter((m) => !(m.id === botId && m.content === '')),
            { id: `e-${Date.now()}`, role: 'error', content: problem },
          ]);
        },
      });

      // Revisó un documento adjunto: debajo de la respuesta, la homologacion
      // con ese mismo documento, a un toque.
      if (puedeHomologar && lecturaP && !fallo && !controller.signal.aborted) {
        setMessages((prev) => prev.map((m) => (m.id === botId && m.content ? { ...m, leyendo: true } : m)));
        const lectura = await lecturaP;
        const ok = !('error' in lectura);
        const detectada = ok && lectura.carreraSugerida && hInfo.mallas.includes(lectura.carreraSugerida) ? lectura.carreraSugerida : null;
        certificados.current.set(botId, { files: outgoing, lecturaId: ok ? lectura.lecturaId : undefined });
        setMessages((prev) =>
          prev.map((m) =>
            m.id === botId && m.content
              ? { ...m, ofrecer: hInfo.mallas, sugerida: detectada, carreraOrigen: ok ? lectura.carreraOrigen : null, leyendo: false }
              : m,
          ),
        );
      }

      abort.current = null;
      setBusy(false);
    },
    [autoGrow, busy, voice, token, files, greeted, hInfo, leerCertificado],
  );

  /** El estudiante eligio la carrera en el chat: homologar con el certificado de ese mensaje. */
  /** Pulso «Consultar mis titulos»: SENESCYT tarda hasta medio minuto. */
  const consultarTitulos = useCallback(
    async (messageId: string, datos: { cedula: string; malla: string | null }) => {
      if (busy) return;
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, consultarCedula: undefined } : m)));
      setBusy(true);
      voice.stop();
      voice.unlock();
      const stamp = Date.now();
      const botId = `a-${stamp}`;
      const espera = 'Consultando la SENESCYT. Puede tardar hasta un minuto.';
      setMessages((prev) => [
        ...prev,
        { id: `u-${stamp}`, role: 'user', content: 'Sí, consulta mis títulos.' },
        { id: botId, role: 'assistant', content: espera },
      ]);
      voice.enqueue(espera);
      try {
        const response = await fetch('/api/consulta-titulos', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ cedula: datos.cedula, malla: datos.malla ?? undefined, consentimiento: true, token: token ?? undefined }),
        });
        const body = (await response.json().catch(() => null)) as (ConsultaTitulos & { error?: string }) | null;
        if (!response.ok || !body || body.error) throw new Error(body?.error ?? 'No pude consultar tus títulos. Inténtalo de nuevo.');
        // Si SENESCYT no respondio, el boton queda para volver a consultar.
        setMessages((prev) =>
          prev.map((m) =>
            m.id === botId ? { ...m, content: body.texto, titulos: body, consultarCedula: body.senescyt ? undefined : { ...datos, otraVez: true } } : m,
          ),
        );
        carreraSugerida.current = body.carreraSugerida;
        for (const sentence of splitSentences(body.texto)) voice.enqueue(sentence);
      } catch (error) {
        setMessages((prev) => [
          ...prev.filter((m) => m.id !== botId),
          { id: `e-${Date.now()}`, role: 'error', content: (error as Error).message },
        ]);
      } finally {
        setBusy(false);
      }
    },
    [busy, token, voice],
  );

  const elegirCarrera = useCallback(
    (messageId: string, malla: string) => {
      const certs = certificados.current.get(messageId);
      if (!certs?.files.length || busy) return;
      setMessages((prev) => prev.map((m) => (m.id === messageId ? { ...m, ofrecer: undefined } : m)));
      certificados.current.delete(messageId);
      void homologarRef.current(
        { nombres: '', cedula: '', correo: '', malla, files: certs.files, lecturaId: certs.lecturaId },
        `Sí, homologa mis estudios en ${titulo(malla)}.`,
      );
    },
    [busy],
  );

  /**
   * Homologacion automatica: el servidor lee el certificado, aplica la
   * plantilla y registra el expediente. El asesor dice el resumen con su voz y
   * la tarjeta con el detalle queda en la conversacion.
   */
  const homologar = useCallback(
    async (s: Solicitud, desdeChat?: string) => {
      if (busy) return;
      setHOpen(false);
      setBusy(true);
      setResumed(false);
      voice.stop();
      voice.unlock(); // gesto del usuario (el clic en Homologar): abre el audio en iOS
      const stamp = Date.now();
      const botId = `a-${stamp}`;
      setMessages((prev) => [
        ...prev,
        desdeChat
          ? // Desde el chat el certificado ya se ve en un mensaje anterior.
            { id: `u-${stamp}`, role: 'user', content: desdeChat }
          : { id: `u-${stamp}`, role: 'user', content: `Quiero homologar mis estudios en ${titulo(s.malla)}.`, files: s.files.map((f) => f.name) },
        { id: botId, role: 'assistant', content: '' },
      ]);
      const controller = new AbortController();
      abort.current = controller;
      try {
        const response = await fetch('/api/homologacion', {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            // Vacios desde el chat: el servidor los toma del certificado.
            nombres: s.nombres || undefined,
            cedula: s.cedula || undefined,
            correo: s.correo || undefined,
            malla: s.malla,
            consentimiento: true, // casilla del formulario o boton del chat
            lecturaId: s.lecturaId,
            token: token ?? undefined,
            attachments: s.files.map(({ name, mediaType, data }) => ({ name, mediaType, data })),
          }),
          signal: controller.signal,
        });
        const body = (await response.json().catch(() => null)) as (Expediente & { error?: string; session?: { token: string } }) | null;
        if (!response.ok || !body || body.error) throw new Error(body?.error ?? 'No se pudo completar la homologación. Inténtalo de nuevo.');
        // La homologacion queda en la conversacion: si era la primera accion,
        // aqui nace el enlace para volver a ella.
        if (body.session?.token) {
          setToken(body.session.token);
          try {
            window.localStorage.setItem(STORAGE_KEY, body.session.token);
          } catch {
            /* sin almacenamiento disponible */
          }
        }
        setMessages((prev) => prev.map((m) => (m.id === botId ? { ...m, content: body.textoHablado, homologacion: body } : m)));
        for (const sentence of splitSentences(body.textoHablado)) voice.enqueue(sentence);
      } catch (error) {
        if ((error as Error).name === 'AbortError') {
          setMessages((prev) => prev.filter((m) => !(m.id === botId && m.content === '')));
        } else {
          setMessages((prev) => [
            ...prev.filter((m) => !(m.id === botId && m.content === '')),
            { id: `e-${Date.now()}`, role: 'error', content: (error as Error).message },
          ]);
        }
      } finally {
        abort.current = null;
        setBusy(false);
      }
    },
    [busy, token, voice],
  );
  homologarRef.current = homologar;

  const addFiles = useCallback(
    async (list: FileList | File[] | null) => {
      if (!list || list.length === 0) return;
      const { files: added, problem } = await readFiles(list, files.length);
      if (added.length) setFiles((prev) => [...prev, ...added].slice(0, MAX_FILES));
      setFileNotice(problem);
    },
    [files.length],
  );

  /** Corta la respuesta en curso: la generacion y la voz. */
  const interrupt = useCallback(() => {
    abort.current?.abort();
    abort.current = null;
    voice.stop();
    setBusy(false);
  }, [voice]);

  // --- Microfono -------------------------------------------------------------
  const sendRef = useRef(send);
  sendRef.current = send;
  const mic = useMic(useCallback((text: string) => void sendRef.current(text), []));

  const onMic = useCallback(() => {
    if (mic.state === 'listening') {
      mic.finish();
      return;
    }
    if (mic.state !== 'idle') return;
    // Hablar mientras el avatar habla lo interrumpe, como en una conversacion.
    interrupt();
    voice.unlock(); // gesto del usuario: abre el audio en iOS
    void mic.start();
  }, [interrupt, mic, voice]);

  // Barra espaciadora mantenida fuera del campo de texto: hablar (escritorio).
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      const typing = (e.target as HTMLElement | null)?.closest('textarea, input, button');
      if (e.code === 'Space' && !typing && !e.repeat && mic.state === 'idle') {
        e.preventDefault();
        onMic();
      }
      if (e.key === 'Escape' && mic.state === 'listening') mic.cancel();
    };
    window.addEventListener('keydown', down);
    return () => window.removeEventListener('keydown', down);
  }, [mic, onMic]);

  const shareLink = useMemo(
    () => (token ? `${window.location.origin}${BASE_PATH}/c/${encodeURIComponent(token)}` : null),
    [token],
  );

  const copyLink = useCallback(async () => {
    if (!shareLink) return;
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2400);
    } catch {
      window.prompt('Copia este enlace para retomar la conversación:', shareLink);
    }
  }, [shareLink]);

  const newConversation = useCallback(() => {
    interrupt();
    introducedRef.current = false;
    carreraSugerida.current = null;
    certificados.current.clear();
    setGreeted(false);
    setNeedsTap(false);
    setMessages([]);
    setToken(null);
    setResumed(false);
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* sin almacenamiento disponible */
    }
    if (window.location.pathname !== (BASE_PATH || '/')) window.history.replaceState(null, '', BASE_PATH || '/');
  }, [interrupt]);

  // --- Presentacion ---------------------------------------------------------
  /**
   * El asesor se presenta con su voz. `auto`: lo pide la apertura del widget,
   * no un clic en esta pagina; si el navegador no deja sonar sin un toque, la
   * presentacion queda escrita y aparece un boton para escucharla.
   */
  const introduce = useCallback(
    (auto: boolean) => {
      if (!intro) {
        pendingOpen.current = auto;
        return;
      }
      if (!introducedRef.current) {
        introducedRef.current = true;
        setGreeted(true);
        setMessages((prev) => (prev.length ? prev : [{ id: `intro-${Date.now()}`, role: 'assistant', content: intro }]));
      }
      if (auto && !canAutoplay()) {
        setNeedsTap(true);
        return;
      }
      setNeedsTap(false);
      voice.stop();
      voice.unlock();
      setWaveCount((n) => n + 1);
      for (const sentence of splitSentences(intro)) voice.enqueue(sentence);
    },
    [intro, voice],
  );

  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const introduceRef = useRef(introduce);
  introduceRef.current = introduce;
  const interruptRef = useRef(interrupt);
  interruptRef.current = interrupt;

  // Widget: la pagina anfitriona avisa cuando se abre, se cierra o se amplia.
  useEffect(() => {
    if (!EMBED) return;
    const onMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; event?: string; expanded?: boolean; mobile?: boolean } | null;
      if (!data || data.type !== 'cec-avatar') return;
      if (data.event === 'open' && !introducedRef.current && messagesRef.current.length === 0) introduceRef.current(true);
      if (data.event === 'close') interruptRef.current();
      if (data.event === 'layout') {
        setExpanded(data.expanded === true);
        setHostMobile(data.mobile === true);
      }
    };
    window.addEventListener('message', onMessage);
    if (window.parent !== window) window.parent.postMessage({ type: 'cec-avatar', action: 'ready' }, '*');
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Si el widget se abrio antes de que llegara la presentacion del servidor.
  useEffect(() => {
    if (intro && pendingOpen.current) {
      pendingOpen.current = false;
      introduce(true);
    }
  }, [intro, introduce]);

  // --- Camara: el avatar ve a la persona (opcional, todo en el navegador) ----
  const busyRef = useRef(busy);
  busyRef.current = busy;
  const speakingRef = useRef(voice.speaking);
  speakingRef.current = voice.speaking;
  const camera = useUserCues({
    // Alguien se sienta delante y aun no hay conversacion: se presenta.
    onArrive: () => {
      if (!introducedRef.current && messagesRef.current.length === 0 && !busyRef.current) introduceRef.current(false);
    },
    // Le saludan con la mano: si no se presento, se presenta; si ya, saluda.
    onWave: () => {
      if (busyRef.current || speakingRef.current) return;
      if (!introducedRef.current && messagesRef.current.length === 0) {
        introduceRef.current(false);
        return;
      }
      setMessages((prev) => [...prev, { id: `wave-${Date.now()}`, role: 'assistant', content: WAVE_REPLY }]);
      voice.unlock();
      voice.enqueue(WAVE_REPLY);
    },
  });
  const cameraLabel =
    camera.state === 'on'
      ? 'Apagar cámara'
      : camera.state === 'starting'
        ? 'Activando…'
        : camera.state === 'denied'
          ? 'Cámara bloqueada'
          : camera.state === 'error'
            ? 'Cámara no disponible'
            : 'Que te vea';

  const status: Status =
    mic.state === 'listening'
      ? 'listening'
      : mic.state === 'transcribing'
        ? 'transcribing'
        : voice.speaking
          ? 'speaking'
          : busy
            ? 'thinking'
            : 'idle';

  const noVoice = voice.voiceName === null && voice.engine === 'webspeech';
  const micAvailable = mic.state !== 'unavailable';

  return (
    <div className={`shell${EMBED ? ' shell--embed' : ''}${GENERIC ? ' shell--generic' : ''}${VARIANT === 'caricatura' ? ' shell--cartoon' : ''}${expanded ? ' is-expanded' : ''}`} data-status={status}>
      <a className="skip" href="#draft">
        Ir al campo de consulta
      </a>
      <div className="backdrop" aria-hidden="true" />

      {EMBED ? (
        <header className="embedbar">
          <span className="embedbar__who">
            <span className="embedbar__face" aria-hidden="true" />
            <span className="embedbar__text">
              <strong>{brand.short}</strong>
              <small>
                <span className="embedbar__dot" aria-hidden="true" />
                {STATUS_LABEL[status]} · Inteligencia artificial
              </small>
            </span>
          </span>
          <span className="embedbar__actions">
            <button
              type="button"
              className="embedbar__btn"
              onClick={voice.toggleMuted}
              disabled={noVoice}
              aria-pressed={voice.muted}
              aria-label={voice.muted ? 'Activar la voz' : 'Silenciar la voz'}
              title={voice.muted ? 'Activar la voz' : 'Silenciar la voz'}
            >
              <Icon name={voice.muted || noVoice ? 'speakerOff' : 'speaker'} />
            </button>
            {hInfo?.disponible ? (
              <button type="button" className="embedbar__btn" onClick={() => setHOpen(true)} disabled={busy} aria-label="Homologar mis estudios" title="Homologar mis estudios">
                <Icon name="cap" />
              </button>
            ) : null}
            <button type="button" className="embedbar__btn" onClick={() => setHumanOpen(true)} aria-label="Hablar con una persona" title="Hablar con una persona">
              <Icon name="person" />
            </button>
            <button
              type="button"
              className="embedbar__btn"
              onClick={newConversation}
              disabled={messages.length === 0}
              aria-label="Nueva conversación"
              title="Nueva conversación"
            >
              <Icon name="plus" />
            </button>
            {hostMobile ? null : (
            <button
              type="button"
              className="embedbar__btn embedbar__btn--expand"
              onClick={() => tellHost(expanded ? 'shrink' : 'expand')}
              aria-label={expanded ? 'Reducir' : 'Ampliar'}
              title={expanded ? 'Reducir' : 'Ampliar'}
            >
              <Icon name={expanded ? 'shrink' : 'expand'} />
            </button>
            )}
            <button type="button" className="embedbar__btn" onClick={() => tellHost('close')} aria-label="Cerrar" title="Cerrar">
              <Icon name="close" />
            </button>
          </span>
        </header>
      ) : (
      <header className="topbar">
        <a className="brand" href="https://cec.edu.ec" rel="noreferrer" aria-label="Centro de Educación Continua">
          <img className="logo logo--day" src="/logoBlue.svg" alt="" width="96" height="35" />
          <img className="logo logo--night" src="/logoWhite.svg" alt="" width="96" height="35" />
          <span className="brand__divider" aria-hidden="true" />
          <span className="brand__text">
            <span className="brand__title">{brand.name}</span>
            <span className="brand__sub">{brand.sub}</span>
          </span>
        </a>

        <div className="topbar__actions">
          <button
            type="button"
            className="icon-btn"
            onClick={voice.toggleMuted}
            disabled={noVoice}
            aria-pressed={voice.muted}
            aria-label={voice.muted ? 'Activar la voz' : 'Silenciar la voz'}
            title={noVoice ? 'Este equipo no tiene voces en español' : voice.muted ? 'Activar la voz' : 'Silenciar la voz'}
          >
            <Icon name={voice.muted || noVoice ? 'speakerOff' : 'speaker'} />
          </button>
          <button
            type="button"
            className="icon-btn"
            onClick={newConversation}
            disabled={messages.length === 0}
            aria-label="Nueva conversación"
            title="Nueva conversación"
          >
            <Icon name="plus" />
          </button>
          {hInfo?.disponible ? (
            <button type="button" className="pill-btn pill-btn--primary" onClick={() => setHOpen(true)} disabled={busy}>
              <Icon name="cap" />
              <span>Homologar mis estudios</span>
            </button>
          ) : null}
          <button type="button" className="pill-btn pill-btn--human" onClick={() => setHumanOpen(true)} aria-label="Hablar con una persona" title="Hablar con una persona">
            <Icon name="person" />
            <span>Hablar con una persona</span>
          </button>
          <button type="button" className="pill-btn" onClick={copyLink} disabled={!shareLink}>
            <Icon name={copied ? 'check' : 'link'} />
            <span>{copied ? 'Enlace copiado' : 'Guardar conversación'}</span>
          </button>
        </div>
      </header>
      )}

      {simulated ? (
        <p className="notice notice--demo" role="note">
          <Icon name="info" />
          {official ? (
            <>
              <span className="notice__long">
                <strong>Versión de prueba.</strong> Orienta según el procedimiento oficial de la
                Secretaría General Técnica, pero todavía no recibe documentos ni emite resoluciones.
              </span>
              <span className="notice__short">
                <strong>Versión de prueba:</strong> orienta, no resuelve trámites.
              </span>
            </>
          ) : (
            <>
              <span className="notice__long">
                <strong>Entorno de demostración.</strong> La información aún no es la oficial de la
                universidad: las respuestas son de ejemplo y no sirven para tomar decisiones académicas.
              </span>
              <span className="notice__short">
                <strong>Demostración:</strong> respuestas de ejemplo, no oficiales.
              </span>
            </>
          )}
        </p>
      ) : null}

      <main className="layout">
        {/* --- Escenario del avatar ------------------------------------------ */}
        <section className="stage" aria-label="Asesor virtual">
          <div className="stage__frame plate__frame" ref={frameRef}>
            {VARIANT === 'caricatura' ? (
              <CartoonAdvisor status={status} viseme={voice.viseme} ampRef={voice.ampRef} waveSignal={waveCount} />
            ) : GENERIC ? (
              <VoiceOrb status={status} ampRef={voice.ampRef} micLevelRef={mic.levelRef} />
            ) : illustrated ? (
              <Avatar character={character} viseme={voice.viseme} speaking={voice.speaking} />
            ) : (
              <PhotoAvatar
                trackRef={voice.trackRef}
                ampRef={voice.ampRef}
                speaking={voice.speaking}
                attentive={busy && !voice.speaking}
                listening={mic.state === 'listening'}
                userRef={camera.cueRef}
                onUnavailable={fallbackToIllustration}
              />
            )}
          </div>
          <div className="stage__shade" aria-hidden="true" />
          {GENERIC ? null : (
          <div className="stage__cam">
            {camera.state === 'on' ? <SelfView videoRef={camera.videoRef} on /> : null}
            <button
              type="button"
              className={`cam-btn${camera.state === 'on' ? ' is-on' : ''}`}
              onClick={() => {
                voice.unlock(); // gesto del usuario: si luego se presenta, que suene
                camera.toggle();
              }}
              disabled={camera.state === 'starting'}
              aria-pressed={camera.state === 'on'}
              title={
                camera.state === 'on'
                  ? 'El asesor te está viendo. Tu imagen no sale de tu equipo.'
                  : 'El asesor te mira, te sigue y responde a tus gestos (sonreír, asentir, saludar). Tu imagen no sale de tu equipo.'
              }
            >
              <Icon name={camera.state === 'on' ? 'cameraOff' : 'camera'} size={18} />
              <span>{cameraLabel}</span>
            </button>
          </div>
          )}
          {/* Subtitulos en directo: la frase que suena ahora. La conversacion ya
              la tiene en texto, asi que no se anuncia dos veces al lector de pantalla. */}
          {subtitles && voice.caption ? (
            <p className="caption stage__caption" aria-hidden="true">
              {voice.caption}
            </p>
          ) : null}
          <button
            type="button"
            className={`cc-btn${subtitles ? ' is-on' : ''}`}
            onClick={toggleSubtitles}
            aria-pressed={subtitles}
            aria-label={subtitles ? 'Ocultar subtítulos' : 'Mostrar subtítulos'}
            title={subtitles ? 'Ocultar subtítulos' : 'Mostrar subtítulos'}
          >
            <Icon name="cc" size={18} />
          </button>
          {needsTap ? (
            <button type="button" className="tap-intro" onClick={() => introduce(false)}>
              <Icon name="play" size={18} />
              <span>Escuchar al asesor</span>
            </button>
          ) : null}

          <div className="stage__top">
            <span className={`status status--${status}`} data-status={status}>
              <span className="status__dot" aria-hidden="true" />
              {STATUS_LABEL[status]}
            </span>
            {voice.speaking ? (
              <span className="bars" aria-hidden="true">
                <i />
                <i />
                <i />
                <i />
              </span>
            ) : null}
          </div>

        </section>

        {/* --- Conversacion ---------------------------------------------------- */}
        <section
          className={`chat${dragging ? ' is-dragging' : ''}`}
          aria-label="Conversación"
          onDragOver={(event) => {
            if (!event.dataTransfer.types.includes('Files')) return;
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={(event) => {
            if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
            setDragging(false);
          }}
          onDrop={(event) => {
            event.preventDefault();
            setDragging(false);
            void addFiles(event.dataTransfer.files);
          }}
        >
          <div className="chat__list" ref={listRef}>
            {resumed ? (
              <p className="notice notice--soft">
                <Icon name="history" />
                <span>Retomaste una conversación anterior desde tu enlace.</span>
              </p>
            ) : null}

            {messages.length === 0 ? (
              <div className="welcome">
                <p className="welcome__eyebrow">Centro de Educación Continua</p>
                <h1 className="welcome__title">
                  Hola, soy tu <span>asesor virtual académico</span>.
                </h1>
                <p className="welcome__lead">
                  Te oriento sobre homologación, carreras, costos y becas de la Universidad
                  Metropolitana, y reviso tus documentos. {micAvailable ? 'Escríbeme o pulsa el micrófono y habla.' : 'Escríbeme tu consulta.'}
                </p>
                <p className="welcome__ai">
                  <Icon name="info" size={16} />
                  <span>
                    Te atiende un asistente de <strong>inteligencia artificial</strong>: orienta, pero no
                    decide. Las decisiones sobre cada caso las toman personas de la universidad.{' '}
                    <button type="button" className="link-btn" onClick={() => setHumanOpen(true)}>
                      Prefiero hablar con una persona
                    </button>
                  </span>
                </p>
                {intro ? (
                  <button type="button" className="listen-btn" onClick={() => introduce(false)}>
                    <Icon name="play" size={18} />
                    <span>Escuchar presentación</span>
                  </button>
                ) : null}
                {hInfo?.disponible ? (
                  <button type="button" className="hcta" onClick={() => setHOpen(true)}>
                    <span className="hcta__icon" aria-hidden="true">
                      <Icon name="cap" size={22} />
                    </span>
                    <span className="hcta__text">
                      <strong>Homologa tus estudios al instante</strong>
                      <span>Sube tu certificado de notas y conoce qué asignaturas te reconocemos.</span>
                    </span>
                    <span className="hcta__go" aria-hidden="true">
                      <Icon name="arrow" />
                    </span>
                  </button>
                ) : null}
                <div className="chips">
                  {(official ? OFFICIAL_SUGGESTIONS : DEMO_SUGGESTIONS).map((s) => (
                    <button key={s.text} type="button" className="chip" onClick={() => void send(s.text)}>
                      <span className="chip__icon" aria-hidden="true">
                        <Icon name={s.icon} />
                      </span>
                      <span className="chip__text">{s.text}</span>
                      <span className="chip__go" aria-hidden="true">
                        <Icon name="arrow" />
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <ol className="messages" aria-live="polite">
                {messages.map((message) => (
                  <li key={message.id} className={`msg msg--${message.role}`}>
                    {message.role === 'assistant' ? <span className="msg__avatar" aria-hidden="true" /> : null}
                    <div className="msg__bubble">
                      <span className="sr-only">
                        {message.role === 'user' ? 'Tú: ' : message.role === 'error' ? 'Aviso: ' : 'Asesor: '}
                      </span>
                      {/* Sin los saltos de linea que el modelo deja antes de las citas. */}
                      {message.role === 'assistant' ? message.content.trimEnd() : message.content}
                      {message.files?.length ? (
                        <span className="msg__files">
                          {message.files.map((name) => (
                            <span key={name} className="file-chip file-chip--sent">
                              <Icon name={/\.pdf$/i.test(name) ? 'doc' : 'image'} size={16} />
                              <span className="file-chip__name">{name}</span>
                            </span>
                          ))}
                        </span>
                      ) : null}
                      {message.sources?.length ? (
                        <span className="msg__sources">
                          <span className="msg__sources-label">
                            <Icon name="book" size={14} />
                            {message.sources.length === 1 ? 'Fuente' : 'Fuentes'}
                          </span>
                          {message.sources.map((s) => (
                            <span key={s} className="source-chip">
                              {s}
                            </span>
                          ))}
                        </span>
                      ) : null}
                      {message.role === 'assistant' && OFRECE_PERSONA.test(message.content) && !(busy && message === messages[messages.length - 1]) ? (
                        <button type="button" className="human-inline" onClick={() => setHumanOpen(true)}>
                          <Icon name="person" size={16} />
                          <span>Hablar con una persona</span>
                        </button>
                      ) : null}
                      {message.homologacion ? <ResultadoCard exp={message.homologacion} /> : null}
                      {message.titulos ? <TitulosCard c={message.titulos} /> : null}
                      {message.leyendo && message.ofrecer === undefined ? (
                        <span className="hleyendo" role="status">
                          <span className="typing" aria-hidden="true">
                            <i />
                            <i />
                            <i />
                          </span>
                          Preparando la homologación con tu certificado…
                        </span>
                      ) : null}
                      {message.consultarCedula ? (
                        <OfertaCedula
                          disabled={busy}
                          otraVez={message.consultarCedula.otraVez}
                          onConsultar={() => void consultarTitulos(message.id, message.consultarCedula!)}
                        />
                      ) : null}
                      {message.ofrecer?.length ? (
                        <OfertaCarreras
                          opciones={message.ofrecer}
                          sugerida={message.sugerida}
                          carreraOrigen={message.carreraOrigen}
                          disabled={busy}
                          onElegir={(m) => elegirCarrera(message.id, m)}
                        />
                      ) : null}
                      {message.role === 'assistant' && busy && message.content === '' ? (
                        <span className="typing" aria-label="Escribiendo">
                          <i />
                          <i />
                          <i />
                        </span>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ol>
            )}
            {messages.length === 1 && messages[0]!.id.startsWith('intro-') && !busy ? (
              <div className="chips chips--after">
                {(official ? OFFICIAL_SUGGESTIONS : DEMO_SUGGESTIONS).map((s) => (
                  <button key={s.text} type="button" className="chip" onClick={() => void send(s.text)}>
                    <span className="chip__icon" aria-hidden="true">
                      <Icon name={s.icon} />
                    </span>
                    <span className="chip__text">{s.text}</span>
                    <span className="chip__go" aria-hidden="true">
                      <Icon name="arrow" />
                    </span>
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <form
            className="composer"
            data-mic={mic.state}
            onSubmit={(event) => {
              event.preventDefault();
              void send(draft);
            }}
          >
            {mic.notice && mic.state === 'idle' ? (
              <p className="composer__notice" role="alert">
                {mic.notice}
              </p>
            ) : null}
            {fileNotice ? (
              <p className="composer__notice" role="alert">
                {fileNotice}
              </p>
            ) : null}
            {files.length ? (
              <div className="composer__files" aria-label="Documentos adjuntos">
                {files.map((f) => (
                  <span key={f.id} className="file-chip">
                    <Icon name={f.mediaType === 'application/pdf' ? 'doc' : 'image'} size={16} />
                    <span className="file-chip__name">{f.name}</span>
                    <span className="file-chip__size">{formatSize(f.size)}</span>
                    <button
                      type="button"
                      className="file-chip__remove"
                      onClick={() => setFiles((prev) => prev.filter((x) => x.id !== f.id))}
                      aria-label={`Quitar ${f.name}`}
                    >
                      <Icon name="close" size={14} />
                    </button>
                  </span>
                ))}
              </div>
            ) : null}

            <div className="composer__bar">
              {mic.state === 'listening' || mic.state === 'transcribing' ? (
                <div className="listening" aria-live="assertive">
                  <MicMeter levelRef={mic.levelRef} active={mic.state === 'listening'} />
                  <span className="listening__label">
                    {mic.state === 'listening' ? 'Te escucho… habla con normalidad' : 'Entendiendo tu pregunta…'}
                  </span>
                  {mic.state === 'listening' ? (
                    <button type="button" className="text-btn" onClick={mic.cancel}>
                      Cancelar
                    </button>
                  ) : null}
                </div>
              ) : (
                <>
                  <label htmlFor="draft" className="sr-only">
                    Tu consulta
                  </label>
                  <textarea
                    id="draft"
                    ref={fieldRef}
                    value={draft}
                    placeholder="Escribe tu consulta…"
                    rows={1}
                    onChange={(event) => {
                      setDraft(event.target.value);
                      autoGrow();
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' && !event.shiftKey) {
                        event.preventDefault();
                        void send(draft);
                      }
                    }}
                  />
                </>
              )}

              {mic.state !== 'listening' && mic.state !== 'transcribing' ? (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPT}
                    multiple
                    hidden
                    onChange={(event) => {
                      void addFiles(event.target.files);
                      event.target.value = '';
                    }}
                  />
                  <button
                    type="button"
                    className="round-btn round-btn--clip"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={busy || files.length >= MAX_FILES}
                    aria-label="Adjuntar documentos"
                    title="Adjuntar documentos (PDF o fotos)"
                  >
                    <Icon name="clip" />
                  </button>
                </>
              ) : null}

              {busy || voice.speaking ? (
                <button type="button" className="round-btn round-btn--stop" onClick={interrupt} aria-label="Detener la respuesta" title="Detener">
                  <Icon name="stop" />
                </button>
              ) : null}

              {micAvailable ? (
                <button
                  type="button"
                  className={`round-btn round-btn--mic${mic.state === 'listening' ? ' is-live' : ''}`}
                  onClick={onMic}
                  disabled={mic.state === 'requesting' || mic.state === 'transcribing'}
                  aria-label={mic.state === 'listening' ? 'Terminar y enviar' : 'Hablar con el asesor'}
                  title={mic.state === 'listening' ? 'Terminar y enviar' : 'Hablar (barra espaciadora)'}
                >
                  <Icon name={mic.state === 'listening' ? 'send' : 'mic'} />
                </button>
              ) : null}

              {mic.state !== 'listening' && mic.state !== 'transcribing' ? (
                <button type="submit" className="round-btn round-btn--send" disabled={(!draft.trim() && files.length === 0) || busy} aria-label="Enviar" title="Enviar">
                  <Icon name="send" />
                </button>
              ) : null}
            </div>

            <p className="composer__hint">
              Las respuestas son orientativas: las decisiones sobre cada caso las toman personas de la universidad.
            </p>
          </form>
        </section>
      </main>

      {reviewMode ? (
        <aside className="review" aria-label="Modo revisión">
          <p className="review__label">Presentación</p>
          <div className="review__options">
            <button type="button" aria-pressed={!illustrated} onClick={() => setIllustrated(false)}>
              Fotográfico
            </button>
            <button type="button" aria-pressed={illustrated} onClick={() => setIllustrated(true)}>
              Ilustrado
            </button>
            {illustrated
              ? CHARACTERS.map((option, index) => (
                  <button
                    key={option.id}
                    type="button"
                    aria-pressed={option.id === character.id}
                    title={option.note}
                    onClick={() => setCharacterId(option.id)}
                  >
                    {String.fromCharCode(65 + index)}
                  </button>
                ))
              : null}
          </div>
        </aside>
      ) : null}

      <HomologacionDialog open={hOpen} info={hInfo} onClose={() => setHOpen(false)} onSubmit={(s) => void homologar(s)} />
      {humanOpen ? (
        <AtencionHumanaDialog
          contacto={humanContact}
          token={token}
          onClose={() => setHumanOpen(false)}
          onRegistrada={(numero) =>
            setMessages((prev) => [
              ...prev,
              {
                id: `h-${Date.now()}`,
                role: 'assistant',
                content: `Registramos tu solicitud ${numero}. Una persona de la universidad te atenderá.`,
              },
            ])
          }
        />
      ) : null}

      <p className="sr-only" role="status" aria-live="polite">
        {STATUS_LABEL[status]}
      </p>
    </div>
  );
}
